import React, { useEffect, useState } from 'react';
import { Trash2, ShoppingBag, Plus } from 'lucide-react';
import marketApi from './marketApi';
import { useCart } from './CartContext';
import { useUsdRate, usdLabel } from './usdRate';

export default function CartPage({ nav }) {
  const { cart, setQty, clearCart, subtotal, lineTotal, lineFreeUnits } = useCart();
  const usdRate = useUsdRate();
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!cart?.items?.length) { setSuggestions([]); return; }
    let alive = true;
    (async () => {
      const exclude = cart.items.map(i => i.product_id).join(',');
      const r = await marketApi.suggestions(cart.supplierId, exclude).catch(() => null);
      if (alive) setSuggestions((r?.products || []).slice(0, 6));
    })();
    return () => { alive = false; };
  }, [cart?.supplierId, cart?.items?.length]);

  if (!cart?.items?.length) {
    return (
      <div className="mk-container">
        <div className="mk-center">
          <ShoppingBag size={44} />
          <b>Votre panier est vide</b>
          <button className="mk-btn primary" onClick={() => nav({ page: 'home' })}>Découvrir le marché</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mk-container">
      <div className="mk-two">
        <div>
          <div className="mk-panel">
            <h3>🏪 {cart.supplierName}</h3>
            {cart.quote_id ? (
              <p className="mk-note" style={{ marginBottom: 8 }}>📋 Devis de la pharmacie : produits et quantités fixés par le pharmacien.</p>
            ) : cart.items.some(i => i.rx_required) ? (
              <p className="mk-note" style={{ marginBottom: 8 }}>
                📋 Ordonnance requise : vous ajouterez sa photo à l'étape suivante.
                {cart.items.some(i => i.rx_original_return) ? " Remettez aussi l'ordonnance ORIGINALE au livreur (frais de retour)." : ''}
              </p>
            ) : null}
            {cart.items.map(item => (
              <div className="mk-line" key={item.line_id}>
                {item.image ? <img src={item.image} alt="" /> : <div className="mk-card-ph" style={{ width: 58, height: 58, borderRadius: 10, background: 'var(--mb-surface-alt)' }}>🍽️</div>}
                <div className="info">
                  <div className="name">{item.name}</div>
                  {(item.options || []).map((o, i) => (
                    <div className="opt" key={i}>+ {o.name}{o.price_delta ? ` (+${o.price_delta.toLocaleString('fr-FR')} FC)` : ''}</div>
                  ))}
                  {item.special_instructions && <div className="opt">📝 {item.special_instructions}</div>}
                  {lineFreeUnits(item) > 0 && (
                    <div className="opt" style={{ color: '#B45309', fontWeight: 700 }}>
                      🎁 {lineFreeUnits(item)} offert{lineFreeUnits(item) > 1 ? 's' : ''} (à 0 FC)
                    </div>
                  )}
                  <div className="price">{lineTotal(item).toLocaleString('fr-FR')} FC</div>
                </div>
                {cart.quote_id ? (
                  <b>× {item.quantity}</b>
                ) : (
                <div className="mk-qty">
                  <button className={item.quantity <= 1 ? 'danger' : ''} onClick={() => setQty(item.line_id, item.quantity - 1)}>
                    {item.quantity <= 1 ? <Trash2 size={15} /> : '−'}
                  </button>
                  <b>{item.quantity}</b>
                  <button onClick={() => {
                    if (item.max_qty_per_order && item.quantity >= item.max_qty_per_order) {
                      window.alert(`Quantité limitée à ${item.max_qty_per_order} par commande pour ce produit.`);
                      return;
                    }
                    setQty(item.line_id, item.quantity + 1);
                  }}>+</button>
                </div>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button className="mk-btn ghost" onClick={() => nav({ page: 'store', supplierId: cart.supplierId })}>
                <Plus size={16} /> Ajouter des articles
              </button>
              <button className="mk-btn ghost" style={{ color: 'var(--mb-danger)' }} onClick={clearCart}>
                <Trash2 size={16} /> Vider
              </button>
            </div>
          </div>

          {!cart.quote_id && suggestions.length > 0 && (
            <div className="mk-panel" style={{ marginTop: 16 }}>
              <h3>Ajoutez-en plus</h3>
              {suggestions.map(p => (
                <div className="mk-line" key={p.product_id}>
                  {(p.image_url || p.image) ? <img src={p.image_thumb || p.image_url || p.image} alt="" /> : <div className="mk-card-ph" style={{ width: 58, height: 58, borderRadius: 10, background: 'var(--mb-surface-alt)' }}>🍽️</div>}
                  <div className="info">
                    <div className="name">{p.name}</div>
                    <div className="price">{((p.effective_price ?? p.promo_price ?? p.price) || 0).toLocaleString('fr-FR')} FC</div>
                  </div>
                  <button className="mk-iconbtn" style={{ background: 'var(--mb-fire)', color: '#fff' }}
                    onClick={() => nav({ page: 'store', supplierId: cart.supplierId })}>+</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mk-panel">
          <h3>Récapitulatif</h3>
          <div className="mk-total"><span>Sous-total</span><span>{subtotal.toLocaleString('fr-FR')} FC</span></div>
          <div className="mk-total"><span>Frais de livraison</span><span>Calculés à l'étape suivante</span></div>
          <div className="mk-total grand"><span>Total articles</span><span>{subtotal.toLocaleString('fr-FR')} FC</span></div>
          {usdLabel(subtotal, usdRate) && <div className="mk-total" style={{ color: 'var(--mb-text-muted, #6B7280)', fontSize: 13 }}><span>Équivalent indicatif</span><span>{usdLabel(subtotal, usdRate)}</span></div>}
          <button className="mk-btn primary block" style={{ marginTop: 14 }} onClick={() => nav({ page: 'checkout' })}>
            Passer la commande
          </button>
        </div>
      </div>
    </div>
  );
}
